import { useEffect, useId } from 'react';
import { Link } from 'react-router-dom';
import type { Environment, Deployment } from '@/types/environments';
import { deploymentsByEnvironment, versionLabel } from '@/types/environments';

export interface RunToolbarOptions {
  /**
   * The stage a test run goes to. Null = the bare URL: the working state
   * before the first publish, the served release after it (task 1236; the
   * service stamps which, task 1235).
   */
  environmentId: string | null;
  concurrency: number;
}

/**
 * What the target rule needs to know (task 1236): whether the account has
 * adopted Publishing & versions, the service's newest published version (null
 * before the first publish), the account's stages and this service's current
 * deployments. `null` in place of the whole object means the versioning
 * feature itself is off: no picker and nothing to say.
 */
export interface RunTargets {
  versioningEnabled: boolean;
  /** e.g. "1.0"; null until the service's first publish. */
  publishedVersion: string | null;
  environments: Environment[];
  deployments: Deployment[];
}

/** One target in state C: a stage with a release, or the built-in default (no `/` stage). */
export interface RunTargetChoice {
  /** Null = the built-in default: the bare URL with no stage holding `/`. */
  environment: Environment | null;
  /** The `environmentId` a run sends. */
  environmentId: string | null;
  label: string;
}

/**
 * The three states a service can be in (task 1236, report "Run targets"):
 *
 * - A, `not-adopted`: the account has not enabled Publishing & versions; runs
 *   use the working state and there is nothing else.
 * - B, `unpublished`: adopted, but this service was never published; runs use
 *   the working state until the first publish.
 * - C, `published`: the stages that hold a release of this service, plus the
 *   built-in default when no stage holds `/`. The working state is not a
 *   target: a published service never serves it from any address. With no
 *   release anywhere, `choices` is empty and nothing can be run.
 */
export type RunTargetState =
  | { kind: 'not-adopted' }
  | { kind: 'unpublished'; hasStages: boolean }
  | { kind: 'published'; choices: RunTargetChoice[]; defaultChoice: RunTargetChoice | null };

/** "Staging — staging · serving v1.3": the label, the path, and what it serves. */
export function environmentTargetLabel(environment: Environment, deployment: Deployment | undefined) {
  const serving = deployment?.resourceVersion
    ? `serving v${versionLabel(deployment.resourceVersion)}`
    : 'no version deployed';
  return `${environment.name} — ${environment.path} · ${serving}`;
}

export function runTargetState(targets: RunTargets): RunTargetState {
  if (!targets.versioningEnabled) return { kind: 'not-adopted' };
  if (!targets.publishedVersion) return { kind: 'unpublished', hasStages: targets.environments.length > 0 };
  const byEnvironment = deploymentsByEnvironment(targets.deployments);
  const choices: RunTargetChoice[] = targets.environments
    .filter((environment) => byEnvironment[environment.id])
    .map((environment) => ({
      environment,
      environmentId: environment.id,
      label: environmentTargetLabel(environment, byEnvironment[environment.id]),
    }));
  const rootStage = targets.environments.find((environment) => environment.path === '/');
  // No stage holds `/`: the bare URL serves the newest published version. Only
  // offered beside a release somewhere, so "nothing released" stays the rule.
  if (!rootStage && choices.length > 0) {
    choices.unshift({ environment: null, environmentId: null, label: `Default — / · serving v${targets.publishedVersion}` });
  }
  const defaultChoice =
    choices.find((choice) => choice.environment?.path === '/') ??
    choices.find((choice) => choice.environment === null) ??
    choices[0] ??
    null;
  return { kind: 'published', choices, defaultChoice };
}

/** True when nothing can be run (state C with no release): the run buttons are disabled. */
export const runTargetsBlocked = (targets: RunTargets | null | undefined) => {
  if (!targets) return false;
  const state = runTargetState(targets);
  return state.kind === 'published' && state.choices.length === 0;
};

/** The reason the run buttons give in that case. */
export const NOTHING_RELEASED =
  'Nothing is released yet. Release a version to a stage on the Releases tab to run tests.';

/**
 * The `environmentId` a run sends for the current options: the picked stage in
 * state C (falling back to its default if the pick is not a choice), the bare
 * URL (null) otherwise.
 */
export function resolveRunTarget(targets: RunTargets | null | undefined, environmentId: string | null): string | null {
  if (!targets) return null;
  const state = runTargetState(targets);
  if (state.kind !== 'published') return null;
  const picked = state.choices.find((choice) => choice.environmentId === environmentId);
  return (picked ?? state.defaultChoice)?.environmentId ?? null;
}

/** The one line states A, B and "nothing released" show in place of a picker. */
export function RunTargetNote({ state, testId = 'run-target-note' }: { state: RunTargetState; testId?: string }) {
  const linkClass = 'font-medium text-indigo-600 dark:text-indigo-400 hover:underline';
  if (state.kind === 'not-adopted') {
    return (
      <span className="text-xs text-gray-500 dark:text-gray-400" data-testid={testId} data-state="not-adopted">
        Runs use the working state.{' '}
        <Link to="/settings/account" className={linkClass}>
          Enable Publishing &amp; versions
        </Link>{' '}
        to run against stages.
      </span>
    );
  }
  if (state.kind === 'unpublished') {
    return (
      <span className="text-xs text-gray-500 dark:text-gray-400" data-testid={testId} data-state="unpublished">
        Runs use the working state until the first publish. Publish this AI Service to run against its stages
        {state.hasStages ? (
          '.'
        ) : (
          <>
            , after adding stages in{' '}
            <Link to="/settings/account" className={linkClass}>
              Settings → Account → Deployment environments
            </Link>
            .
          </>
        )}
      </span>
    );
  }
  if (state.choices.length === 0) {
    return (
      <span className="text-xs text-amber-700 dark:text-amber-300" data-testid={testId} data-state="nothing-released">
        {NOTHING_RELEASED}
      </span>
    );
  }
  return null;
}

interface RunOptionsControlsProps {
  options: RunToolbarOptions;
  onOptionsChange: (options: RunToolbarOptions) => void;
  /** The target rule's inputs (task 1236); null while the versioning feature is off. */
  targets?: RunTargets | null;
}

/** Bounded concurrency (report section 4): sequential by default, never unbounded. */
export const CONCURRENCY_CHOICES = [1, 2, 3] as const;

/**
 * Where a test run goes and how many tests run at once (task 1209): **Run on**
 * and **At a time**. Shared by the selection toolbar and the Suites block so
 * the two cannot drift. **Run on** follows the three-state rule (task 1236):
 * a picker of released stages once the service is published, with the `/`
 * holder preselected; a one-line note in its place otherwise.
 */
export function RunOptionsControls({ options, onOptionsChange, targets }: RunOptionsControlsProps) {
  // Both the toolbar and the Suites block can be on screen at once: unique ids.
  const id = useId();
  const state = targets ? runTargetState(targets) : null;
  const choices = state?.kind === 'published' ? state.choices : [];
  const defaultId = state?.kind === 'published' ? state.defaultChoice?.environmentId ?? null : null;
  const isChoice = choices.some((choice) => choice.environmentId === options.environmentId);
  const selectClass =
    'rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-xs text-gray-900 dark:text-white';

  // Land on the default target when the current value is not one of the
  // choices (the working state is never one in state C).
  useEffect(() => {
    if (state?.kind !== 'published' || choices.length === 0 || isChoice) return;
    if (options.environmentId !== defaultId) onOptionsChange({ ...options, environmentId: defaultId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.kind, choices.length, isChoice, defaultId]);

  return (
    <>
      {state && state.kind === 'published' && choices.length > 0 ? (
        <span className="flex items-center gap-1.5">
          <label htmlFor={`${id}-target`} className="text-xs text-gray-600 dark:text-gray-300">
            Run on
          </label>
          <select
            id={`${id}-target`}
            value={isChoice ? options.environmentId ?? '' : defaultId ?? ''}
            onChange={(e) => onOptionsChange({ ...options, environmentId: e.target.value || null })}
            className={selectClass}
          >
            {choices.map((choice) => (
              <option key={choice.environmentId ?? 'default'} value={choice.environmentId ?? ''}>
                {choice.label}
              </option>
            ))}
          </select>
        </span>
      ) : (
        state && <RunTargetNote state={state} />
      )}

      <span className="flex items-center gap-1.5">
        <label htmlFor={`${id}-concurrency`} className="text-xs text-gray-600 dark:text-gray-300">
          At a time
        </label>
        <select
          id={`${id}-concurrency`}
          value={options.concurrency}
          onChange={(e) => onOptionsChange({ ...options, concurrency: Number(e.target.value) })}
          className={selectClass}
        >
          {CONCURRENCY_CHOICES.map((choice) => (
            <option key={choice} value={choice}>
              {choice === 1 ? '1 (sequential)' : choice}
            </option>
          ))}
        </select>
      </span>
    </>
  );
}

export default RunOptionsControls;
